import { translate } from '../../i18n/index.js';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { AdminReferral, AdminReferralStatus, ReferralSettings } from '@atomfit/shared';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { CustomSelect, Spinner, Tag, formatCurrency } from '../../components/design/AtomFitUI.js';
import { adminApi } from '../../api/admin.api.js';
import { useAuth } from '../../hooks/useAuth.js';

const statusLabels: Record<AdminReferralStatus, string> = {
  PENDING: translate('pages.admin.admin_referrals_page.status_labels.pending'),
  ACTIVATED: translate('pages.admin.admin_referrals_page.status_labels.activated'),
  PAID_OUT: translate('pages.admin.admin_referrals_page.status_labels.paid_out'),
};

function accountLabel(account: AdminReferral['referrerAccount']) {
  return [account.firstName, account.lastName].filter(Boolean).join(' ') || account.email;
}

function formatDate(value: string | Date | null) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function toDisplayAmount(amountInSmallestUnit: number) {
  return String(amountInSmallestUnit / 100);
}

function toSmallestUnit(value: string) {
  const normalized = Number(value.replace(',', '.'));
  if (!Number.isFinite(normalized) || normalized < 0) return 0;
  return Math.round(normalized * 100);
}

export function AdminReferralsPage() {
  const { role } = useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const [settings, setSettings] = useState<ReferralSettings | null>(null);
  const [referrals, setReferrals] = useState<AdminReferral[]>([]);
  const [counts, setCounts] = useState({ pending: 0, activated: 0, paidOut: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState<AdminReferralStatus | ''>('');
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    referrerCreditAmount: '0',
    refereeCreditAmount: '0',
    currency: 'CZK',
  });

  const load = useCallback(async () => {
    if (!isPlatform) return;
    setLoading(true);
    try {
      const res = await adminApi.listReferrals({
        page,
        ...(status ? { status } : {}),
        ...(query ? { q: query } : {}),
      });
      setSettings(res.data.settings);
      setReferrals(res.data.referrals.data);
      setCounts(res.data.counts);
      setTotalPages(res.data.referrals.meta.totalPages);
      setForm({
        referrerCreditAmount: toDisplayAmount(res.data.settings.referrerCreditAmountInSmallestUnit),
        refereeCreditAmount: toDisplayAmount(res.data.settings.refereeCreditAmountInSmallestUnit),
        currency: res.data.settings.currency,
      });
    } finally {
      setLoading(false);
    }
  }, [isPlatform, page, query, status]);

  useEffect(() => {
    void load();
  }, [load]);

  function applySearch(event: React.FormEvent) {
    event.preventDefault();
    setPage(1);
    setQuery(queryInput.trim());
  }

  async function saveSettings(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      const res = await adminApi.updateReferralSettings({
        referrerCreditAmountInSmallestUnit: toSmallestUnit(form.referrerCreditAmount),
        refereeCreditAmountInSmallestUnit: toSmallestUnit(form.refereeCreditAmount),
        currency: form.currency.trim().toUpperCase(),
      });
      setSettings(res.data);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout
      title={translate(
        'pages.admin.admin_referrals_page.admin_referrals_page.admin_layout.referral',
      )}
      eyebrow={translate(
        'pages.admin.admin_referrals_page.admin_referrals_page.admin_layout.eyebrow',
      )}
    >
      {!isPlatform ? (
        <div className="af-card" style={{ padding: 24, color: 'var(--af-muted)' }}>
          {translate('pages.admin.admin_referrals_page.admin_referrals_page.div_card.referral')}
        </div>
      ) : (
        <>
          <div
            className="af-admin-referral-overview"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(280px, 0.85fr) minmax(320px, 1.15fr)',
              gap: 16,
              marginBottom: 16,
            }}
          >
            <form className="af-card" style={{ padding: 18 }} onSubmit={saveSettings}>
              <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 4 }}>
                {translate('pages.admin.admin_referrals_page.admin_referrals_page.div.credit_edit')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--af-muted)', marginBottom: 16 }}>
                {translate(
                  'pages.admin.admin_referrals_page.admin_referrals_page.div.credit_referral_edit',
                )}
              </div>
              <div style={{ display: 'grid', gap: 12 }}>
                <label className="af-label">
                  {translate(
                    'pages.admin.admin_referrals_page.admin_referrals_page.label_label.credit_referral_edit',
                  )}

                  <input
                    className="af-input"
                    inputMode="decimal"
                    value={form.referrerCreditAmount}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        referrerCreditAmount: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="af-label">
                  {translate(
                    'pages.admin.admin_referrals_page.admin_referrals_page.label_label.user_credit_edit',
                  )}

                  <input
                    className="af-input"
                    inputMode="decimal"
                    value={form.refereeCreditAmount}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        refereeCreditAmount: event.target.value,
                      }))
                    }
                  />
                </label>
                <label className="af-label">
                  {translate(
                    'pages.admin.admin_referrals_page.admin_referrals_page.label_label.currency',
                  )}

                  <input
                    className="af-input"
                    value={form.currency}
                    maxLength={3}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, currency: event.target.value }))
                    }
                  />
                </label>
                <button className="af-primary-btn" type="submit" disabled={saving}>
                  {saving
                    ? translate(
                        'pages.admin.admin_referrals_page.admin_referrals_page.button_primary_btn.label',
                      )
                    : saved
                      ? translate(
                          'pages.admin.admin_referrals_page.admin_referrals_page.button_primary_btn.save',
                        )
                      : translate(
                          'pages.admin.admin_referrals_page.admin_referrals_page.button_primary_btn.save_2',
                        )}
                </button>
              </div>
              {settings && (
                <div style={{ color: 'var(--af-muted)', fontSize: 12, marginTop: 12 }}>
                  {translate('pages.admin.admin_referrals_page.admin_referrals_page.div.date')}
                  {formatDate(settings.updatedAt)}
                </div>
              )}
            </form>

            <div className="af-card" style={{ padding: 18 }}>
              <div className="af-admin-referral-metrics">
                <ReferralMetric
                  label={translate(
                    'pages.admin.admin_referrals_page.admin_referrals_page.referral_metric.pending',
                  )}
                  value={counts.pending}
                />

                <ReferralMetric
                  label={translate(
                    'pages.admin.admin_referrals_page.admin_referrals_page.referral_metric.label',
                  )}
                  value={counts.activated}
                />

                <ReferralMetric
                  label={translate(
                    'pages.admin.admin_referrals_page.admin_referrals_page.referral_metric.label_2',
                  )}
                  value={counts.paidOut}
                />
              </div>
            </div>
          </div>

          <div className="af-card" style={{ padding: 16, marginBottom: 16 }}>
            <form
              className="af-admin-referral-filters"
              onSubmit={applySearch}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(220px, 1fr) 220px auto',
                gap: 12,
                alignItems: 'end',
              }}
            >
              <label className="af-label">
                {translate(
                  'pages.admin.admin_referrals_page.admin_referrals_page.label_label.search',
                )}

                <input
                  className="af-input"
                  value={queryInput}
                  onChange={(event) => setQueryInput(event.target.value)}
                  placeholder={translate(
                    'pages.admin.admin_referrals_page.admin_referrals_page.input_input.placeholder_user_email_referral',
                  )}
                />
              </label>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_referrals_page.admin_referrals_page.label_label.status',
                )}

                <CustomSelect
                  value={status}
                  onChange={(value) => {
                    setPage(1);
                    setStatus(value as AdminReferralStatus | '');
                  }}
                  options={[
                    {
                      value: '',
                      label: translate(
                        'pages.admin.admin_referrals_page.admin_referrals_page.custom_select.options',
                      ),
                    },
                    {
                      value: 'PENDING',
                      label: translate(
                        'pages.admin.admin_referrals_page.admin_referrals_page.custom_select.options_2',
                      ),
                    },
                    {
                      value: 'ACTIVATED',
                      label: translate(
                        'pages.admin.admin_referrals_page.admin_referrals_page.custom_select.options_3',
                      ),
                    },
                    {
                      value: 'PAID_OUT',
                      label: translate(
                        'pages.admin.admin_referrals_page.admin_referrals_page.custom_select.options_4',
                      ),
                    },
                  ]}
                />
              </label>
              <button className="af-outline-btn" style={{ padding: '12px 16px' }} type="submit">
                {translate(
                  'pages.admin.admin_referrals_page.admin_referrals_page.button_outline_btn.filter',
                )}
              </button>
            </form>
          </div>

          <div className="af-card" style={{ overflow: 'auto' }}>
            <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--af-border)' }}>
              <div style={{ fontSize: 16, fontWeight: 900 }}>
                {translate('pages.admin.admin_referrals_page.admin_referrals_page.div.referral')}
              </div>
              <div style={{ fontSize: 12, color: 'var(--af-muted)', marginTop: 4 }}>
                {translate(
                  'pages.admin.admin_referrals_page.admin_referrals_page.div.user_referral',
                )}
              </div>
            </div>
            <ReferralTable referrals={referrals} loading={loading} />
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button
                className="af-outline-btn"
                style={{ padding: '8px 16px' }}
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
              >
                {translate(
                  'pages.admin.admin_referrals_page.admin_referrals_page.button_outline_btn.previous',
                )}
              </button>
              <span style={{ padding: '8px 12px', fontSize: 13, color: 'var(--af-muted)' }}>
                {page} {'/'} {totalPages}
              </span>
              <button
                className="af-outline-btn"
                style={{ padding: '8px 16px' }}
                disabled={page >= totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                {translate(
                  'pages.admin.admin_referrals_page.admin_referrals_page.button_outline_btn.next',
                )}
              </button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}

function ReferralMetric({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ border: '1px solid var(--af-border)', borderRadius: 8, padding: 14 }}>
      <div style={{ color: 'var(--af-muted)', fontSize: 11, fontWeight: 800, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 900 }}>{value}</div>
    </div>
  );
}

function ReferralTable({ referrals, loading }: { referrals: AdminReferral[]; loading: boolean }) {
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: 18,
          color: 'var(--af-muted)',
        }}
      >
        <Spinner />{' '}
        {translate('pages.admin.admin_referrals_page.referral_table.div.loading_referral')}
      </div>
    );
  }

  if (referrals.length === 0) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--af-muted)' }}>
        {translate('pages.admin.admin_referrals_page.referral_table.div.referral')}
      </div>
    );
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--af-border)' }}>
          {[
            translate('pages.admin.admin_referrals_page.referral_table.tr.referral'),
            translate('pages.admin.admin_referrals_page.referral_table.tr.user'),
            translate('pages.admin.admin_referrals_page.referral_table.tr.status'),
            translate('pages.admin.admin_referrals_page.referral_table.tr.copy'),
            translate('pages.admin.admin_referrals_page.referral_table.tr.copy_2'),
            translate('pages.admin.admin_referrals_page.referral_table.tr.create'),
          ].map((heading) => (
            <th
              key={heading}
              style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontSize: 10,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--af-muted)',
                whiteSpace: 'nowrap',
              }}
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {referrals.map((referral, index) => (
          <tr
            key={referral.id}
            style={{
              borderBottom: index < referrals.length - 1 ? '1px solid var(--af-border)' : 'none',
            }}
          >
            <td style={{ padding: '12px 16px' }}>
              <div style={{ fontWeight: 800 }}>{accountLabel(referral.referrerAccount)}</div>
              <div style={{ color: 'var(--af-muted)', fontSize: 12 }}>
                {referral.referrerAccount.email}
              </div>
            </td>
            <td style={{ padding: '12px 16px' }}>
              <div style={{ fontWeight: 800 }}>{accountLabel(referral.refereeAccount)}</div>
              <div style={{ color: 'var(--af-muted)', fontSize: 12 }}>
                {referral.refereeAccount.email}
              </div>
            </td>
            <td style={{ padding: '12px 16px' }}>
              <Tag
                color={
                  referral.status === 'PAID_OUT'
                    ? 'var(--af-success)'
                    : referral.status === 'ACTIVATED'
                      ? 'var(--af-accent)'
                      : 'var(--af-muted)'
                }
                bg={
                  referral.status === 'PAID_OUT'
                    ? 'rgba(74,222,128,0.1)'
                    : referral.status === 'ACTIVATED'
                      ? 'var(--af-accent-soft)'
                      : 'rgba(255,255,255,0.05)'
                }
              >
                {statusLabels[referral.status]}
              </Tag>
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)' }}>
              <div>{formatDate(referral.convertedAt)}</div>
              {referral.convertedBooking && (
                <div style={{ fontSize: 12 }}>
                  {referral.convertedBooking.gym.name} {'·'}{' '}
                  {formatCurrency(
                    referral.convertedBooking.priceInSmallestUnit,
                    referral.convertedBooking.currency,
                  )}
                </div>
              )}
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)', whiteSpace: 'nowrap' }}>
              {referral.paidOutAmountInSmallestUnit
                ? formatCurrency(referral.paidOutAmountInSmallestUnit, referral.currency)
                : '-'}
              {referral.paidOutAt && (
                <div style={{ fontSize: 12 }}>{formatDate(referral.paidOutAt)}</div>
              )}
            </td>
            <td style={{ padding: '12px 16px', color: 'var(--af-muted)', whiteSpace: 'nowrap' }}>
              {formatDate(referral.createdAt)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
